import { Box, Typography, useTheme, Chip } from "@mui/material";
import { font } from "../../../../styles/font";
import dayjs from "dayjs";

export default function UserMessageContainer({ data }) {
    const theme = useTheme();

    console.log("UserMessageContainer data:", data);

    // Get message content
    const getMessageContent = () => {
        return data?.prompt || data?.chatMessageStr || data?.message || "No message content";
    };

    const messageContent = getMessageContent();

    // Helper function to render model chip (if available)
    const renderModelChip = () => {
        if (!data?.model) return null;
        return (
            <Chip 
                label={data.model}
                size="small"
                sx={{
                    height: '16px',
                    fontSize: '0.5rem',
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? theme.palette.grey[700] 
                        : theme.palette.grey[200],
                    color: theme.palette.text.secondary,
                    '& .MuiChip-label': {
                        px: 0.5,
                    },
                }}
            />
        );
    };

    return (
        <Box className={`flex flex-col items-end w-full px-2 mb-3`}>
            {/* Simple user message bubble with animations */}
            <Box 
                className={`flex flex-col gap-1 px-3 py-2 rounded-lg w-fit max-w-[80%] transition-all duration-300 ease-out`} 
                sx={{ 
                    color: theme.palette.mode === 'dark' 
                        ? theme.palette.common.white 
                        : theme.palette.text.primary,
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? theme.palette.grey[800] 
                        : "white",
                    borderRadius: '12px 12px 4px 12px',
                    border: `1px solid ${
                        theme.palette.mode === 'dark' 
                            ? theme.palette.grey[700] 
                            : theme.palette.grey[300]
                    }`,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 1px 2px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.3)'
                        : '0 1px 2px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.05)',
                    transform: 'translateY(0)',
                    opacity: 1,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        boxShadow: theme.palette.mode === 'dark'
                            ? '0 2px 4px rgba(0,0,0,0.3), 0 2px 6px rgba(0,0,0,0.4)'
                            : '0 2px 4px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.08)',
                        transform: 'translateY(-1px)',
                        borderColor: theme.palette.primary.light,
                    }
                }}
            >
                {/* Header with user name and optional model chip */}
                <Box className="flex items-center justify-between w-full gap-2">
                    <Typography 
                        fontSize={font.size.fs_6} 
                        fontWeight={600}
                        sx={{ 
                            color: theme.palette.mode === 'dark' 
                                ? theme.palette.grey[400] 
                                : theme.palette.text.secondary,
                        }}
                    >
                        You
                    </Typography>
                    {renderModelChip()}
                </Box>

                {/* Message content with fade-in */}
                <Typography 
                    fontSize={font.size.fs_5} 
                    sx={{ 
                        color: theme.palette.mode === 'dark' 
                            ? theme.palette.common.white 
                            : theme.palette.text.primary,
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        animation: 'fadeIn 0.4s ease-out',
                        '@keyframes fadeIn': {
                            from: {
                                opacity: 0,
                                transform: 'translateY(5px)'
                            },
                            to: {
                                opacity: 1,
                                transform: 'translateY(0)'
                            }
                        }
                    }}
                >
                    {messageContent}
                </Typography>

                {/* Timestamp with subtle animation */}
                <Typography 
                    fontSize={font.size.fs_6} 
                    sx={{ 
                        color: theme.palette.mode === 'dark' 
                            ? theme.palette.grey[400] 
                            : theme.palette.text.secondary,
                        textAlign: 'right',
                        mt: 0.5,
                        opacity: 0.6,
                        transition: 'opacity 0.2s ease',
                        '&:hover': {
                            opacity: 0.8,
                        }
                    }}
                >
                    {dayjs(data?.createdAt || new Date()).format("hh:mm A")}
                </Typography>
            </Box>
        </Box>
    );
}